import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Avatar from './Avatar';

const navItems = [
  { path: '/',              label: 'Dashboard',      icon: '📊' },
  { path: '/operations',    label: 'Opérations',     icon: '🧭' },
  { path: '/analytics',     label: 'Analytics',      icon: '📈' },
  { path: '/heatmap',       label: 'Heatmap',        icon: '🗺️' },
  { path: '/moderation',    label: 'Modération',     icon: '🚩' },
  { path: '/referrals',     label: 'Parrainages',    icon: '🎁' },
  { path: '/logs',          label: 'Logs',           icon: '📜' },
  { path: '/users',         label: 'Utilisateurs',   icon: '👥' },
  { path: '/devices',       label: 'Appareils',      icon: '📱' },
  { path: '/categories',    label: 'Catégories',     icon: '🗂️' },
  { path: '/subscriptions', label: 'Abonnements',    icon: '💳' },
  { path: '/payments',      label: 'Transactions',   icon: '💰' },
  { path: '/reports',       label: 'Rapports',       icon: '📋' },
  { path: '/settings',      label: 'Paramètres',     icon: '⚙️' },
];

function NavLinkItem({ item, active }) {
  const [hover, setHover] = useState(false);
  const baseStyle = {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '10px 14px', borderRadius: 10,
    color: active ? '#FFFFFF' : 'rgba(255,255,255,0.78)',
    cursor: 'pointer',
    transition: 'all 0.18s cubic-bezier(0.4, 0, 0.2, 1)',
    marginBottom: 4,
    position: 'relative',
    background: active
      ? 'rgba(255,255,255,0.18)'
      : hover ? 'rgba(255,255,255,0.10)' : 'transparent',
    transform: hover ? 'translateX(4px)' : 'translateX(0)',
    boxShadow: active ? '0 4px 12px rgba(0,0,0,0.12)' : 'none',
    borderLeft: active ? '3px solid #FBBF24' : '3px solid transparent',
    paddingLeft: 11,
  };
  return (
    <Link to={item.path} style={{ textDecoration: 'none' }}>
      <div
        style={baseStyle}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <span style={{
          fontSize: 18, width: 22, textAlign: 'center',
          transform: hover ? 'scale(1.18)' : 'scale(1)',
          transition: 'transform 0.18s',
          display: 'inline-block',
        }}>{item.icon}</span>
        <span style={{
          fontSize: 13.5,
          fontWeight: active ? 700 : 500,
          letterSpacing: 0.2,
        }}>{item.label}</span>
        {active && (
          <span style={{
            position: 'absolute', right: 12,
            width: 6, height: 6, borderRadius: 3,
            background: '#FBBF24',
            boxShadow: '0 0 8px rgba(251,191,36,0.8)',
          }} />
        )}
      </div>
    </Link>
  );
}

export default function Layout({ children }) {
  const { logout, user } = useAuth();
  const { pathname } = useLocation();
  const [logoutHover, setLogoutHover] = useState(false);

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <aside style={S.sidebar}>
        {/* Logo */}
        <div style={S.logoWrap}>
          <img src="/logo.png" alt="Elite Services" style={S.logoImg} />
          <div>
            <div style={S.logoText}>Elite Services</div>
            <div style={S.logoSub}>Administration</div>
          </div>
        </div>

        <div style={S.divider} />

        {/* Nav scrollable */}
        <nav style={S.nav}>
          {navItems.map(item => (
            <NavLinkItem
              key={item.path}
              item={item}
              active={pathname === item.path || (item.path !== '/' && pathname.startsWith(item.path))}
            />
          ))}
        </nav>

        {/* Admin profile */}
        <div style={S.adminProfile}>
          <Avatar name={user?.name || 'Admin'} size="sm" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.name || 'Administrateur'}
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)' }}>Admin</div>
          </div>
          <button
            onClick={logout}
            title="Déconnexion"
            style={{
              ...S.logoutBtn,
              background: logoutHover ? 'rgba(255,255,255,0.18)' : 'transparent',
              transform: logoutHover ? 'scale(1.1)' : 'scale(1)',
            }}
            onMouseEnter={() => setLogoutHover(true)}
            onMouseLeave={() => setLogoutHover(false)}
          >
            ⏻
          </button>
        </div>
      </aside>

      {/* Main */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Top header */}
        <header style={S.header}>
          <div>
            <h1 style={{ fontSize: 18, fontWeight: 700 }}>
              {navItems.find(n => pathname === n.path || (n.path !== '/' && pathname.startsWith(n.path)))?.label || 'Dashboard'}
            </h1>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={S.headerBadge}>🟢 Système opérationnel</div>
          </div>
        </header>

        <main style={{ flex: 1, padding: '28px 32px', overflowY: 'auto' }} className="animate-fade">
          {children}
        </main>
      </div>
    </div>
  );
}

const S = {
  sidebar: {
    width: 'var(--sidebar-width)',
    background: 'linear-gradient(180deg, #2563EB 0%, #1D4ED8 50%, #1E40AF 100%)',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    position: 'sticky',
    top: 0,
    height: '100vh',
    boxShadow: '4px 0 24px rgba(30, 64, 175, 0.18)',
  },
  logoWrap: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '22px 20px 18px',
  },
  logoImg: {
    width: 40, height: 40, borderRadius: 12,
    objectFit: 'contain',
    background: '#FFFFFF',
    padding: 4,
    boxShadow: '0 6px 16px rgba(0,0,0,0.18)',
  },
  logoText: { fontSize: 15, fontWeight: 800, color: '#fff', letterSpacing: 0.3 },
  logoSub: { fontSize: 10.5, color: 'rgba(255,255,255,0.7)', marginTop: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 },
  divider: { height: 1, background: 'rgba(255,255,255,0.15)', margin: '0 16px 12px' },
  nav: {
    flex: 1,
    padding: '4px 12px 16px',
    overflowY: 'auto',
    overflowX: 'hidden',
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(255,255,255,0.25) transparent',
  },
  adminProfile: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '14px 16px',
    borderTop: '1px solid rgba(255,255,255,0.12)',
    background: 'rgba(0,0,0,0.10)',
    backdropFilter: 'blur(8px)',
  },
  logoutBtn: {
    border: 'none', cursor: 'pointer',
    color: '#fff', fontSize: 16,
    padding: 6, borderRadius: 8,
    transition: 'all 0.2s ease',
    minWidth: 32, minHeight: 32,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  header: {
    height: 'var(--header-height)',
    background: 'var(--surface)',
    borderBottom: '1px solid var(--border)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0 32px',
    position: 'sticky', top: 0, zIndex: 100,
    boxShadow: 'var(--shadow-sm)',
  },
  headerBadge: {
    fontSize: 12, color: 'var(--success)',
    background: 'var(--success-light)',
    padding: '4px 12px', borderRadius: 'var(--radius-full)',
    fontWeight: 600,
  },
};
